import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/site/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, bikeImage, formatApiErrorDetail } from "@/api";

const BRANDS = ["Honda", "Hero", "TVS", "Bajaj", "Suzuki", "Yamaha", "Royal Enfield", "KTM", "Vespa", "Other"];

const empty = {
  title: "", brand: "Honda", category: "Bike", year: "", km: "", fuel: "Petrol",
  price: "", location: "", image: "", description: "", featured: false, status: "available",
};

const BikeForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").catch(() => navigate("/admin/login"));
    if (isEdit) {
      api.get(`/bikes/${id}`)
        .then(({ data }) => setForm({
          title: data.title, brand: data.brand, category: data.category,
          year: String(data.year), km: String(data.km), fuel: data.fuel,
          price: String(data.price), location: data.location || "",
          image: data.image || "", description: data.description || "",
          featured: !!data.featured, status: data.status,
        }))
        .catch(() => {
          toast.error("Listing nahi mili");
          navigate("/admin");
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, navigate]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", data.path);
      toast.success("Photo upload ho gayi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      year: Number(form.year),
      km: Number(form.km),
      price: Number(form.price),
    };
    try {
      if (isEdit) {
        await api.put(`/bikes/${id}`, payload);
        toast.success("Listing update ho gayi");
      } else {
        await api.post("/bikes", payload);
        toast.success("Nayi listing add ho gayi");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#050505] min-h-screen flex items-center justify-center">
        <LogoMark className="w-12 h-12 text-white animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="bike-form-page">
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <span className="flex items-center gap-2.5">
            <LogoMark className="w-8 h-8 text-white" />
            <span className="font-display font-semibold uppercase tracking-tight">{isEdit ? "Edit Listing" : "New Listing"}</span>
          </span>
          <Link to="/admin" data-testid="bike-form-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 hover:text-volt transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 py-10">
        <form onSubmit={handleSubmit} className="border border-white/10 bg-card p-7 md:p-9 space-y-7" data-testid="bike-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">Title *</Label>
              <Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="bike-title-input" placeholder="e.g. KTM Duke 390" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">Brand *</Label>
              <Select value={form.brand} onValueChange={(v) => set("brand", v)}>
                <SelectTrigger data-testid="bike-brand-select" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10 text-white">
                  {BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">Category *</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="bike-category-select" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10 text-white">
                  <SelectItem value="Bike">Bike</SelectItem>
                  <SelectItem value="Scooter">Scooter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">Year *</Label>
              <Input required type="number" min="1990" max="2026" value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="bike-year-input" placeholder="2022" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">KM Driven *</Label>
              <Input required type="number" min="0" value={form.km} onChange={(e) => set("km", e.target.value)} data-testid="bike-km-input" placeholder="8500" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">Price (₹) *</Label>
              <Input required type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="bike-price-input" placeholder="150000" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">Fuel</Label>
              <Select value={form.fuel} onValueChange={(v) => set("fuel", v)}>
                <SelectTrigger data-testid="bike-fuel-select" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10 text-white">
                  <SelectItem value="Petrol">Petrol</SelectItem>
                  <SelectItem value="Electric">Electric</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">Location</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="bike-location-input" placeholder="Showroom Floor A" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-widest text-white/50">Photo</Label>
            {form.image && (
              <div className="border border-white/10 overflow-hidden max-w-sm">
                <img src={bikeImage(form.image)} alt="Bike preview" className="w-full aspect-[4/3] object-cover" data-testid="bike-image-preview" />
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="bike-upload-button"
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 text-xs font-semibold uppercase tracking-widest hover:border-volt hover:text-volt disabled:opacity-50 transition-colors duration-300"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                {uploading ? "Uploading..." : "Upload Photo"}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" data-testid="bike-upload-input" />
              <Input
                value={form.image}
                onChange={(e) => set("image", e.target.value)}
                data-testid="bike-image-url-input"
                placeholder="ya image URL paste karo"
                className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12 flex-1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-white/50">Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="bike-description-input" placeholder="Condition, owners, service history..." className="bg-[#0a0a0a] border-white/15 text-white rounded-none min-h-[100px]" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex items-center justify-between border border-white/10 bg-[#0a0a0a] px-5 py-4">
              <Label className="text-xs uppercase tracking-widest text-white/50">Featured Listing</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="bike-featured-switch" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/50">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="bike-status-select" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10 text-white">
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || uploading}
            data-testid="bike-save-button"
            className="w-full bg-volt text-black font-semibold uppercase tracking-widest py-4 hover:brightness-110 disabled:opacity-50 transition-[filter] duration-300"
          >
            {saving ? "Saving..." : isEdit ? "Update Listing" : "Add Listing"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default BikeForm;
